import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ArrowRight, ArrowLeft, Info, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const DirectionBadge = ({ direction }) => {
  if (direction === 'system') return <Badge variant="outline" className="font-mono text-[10px]">ECU EVENT</Badge>;
  if (direction === 'request') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-autoxium-blue">
        Tester <ArrowRight className="h-3 w-3" /> ECU
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">
      ECU <ArrowLeft className="h-3 w-3" /> Tester
    </span>
  );
};

const LogEntry = ({ entry, expanded, onToggle }) => {
  const borderColor = entry.direction === 'response' ? (entry.positive ? 'border-l-green-600' : 'border-l-destructive') : 'border-l-autoxium-blue';
  return (
    <div
      className={`border-l-4 ${entry.direction === 'system' ? 'border-l-muted-foreground/40' : borderColor} bg-muted/30 rounded-r-md`}
      data-testid="uds-log-entry"
    >
      <button
        type="button"
        onClick={entry.breakdown ? onToggle : undefined}
        className={`w-full text-left px-3 py-2 flex items-start gap-3 ${entry.breakdown ? 'cursor-pointer hover:bg-muted/50' : ''}`}
      >
        <span className="font-mono text-[11px] text-muted-foreground shrink-0 pt-0.5 w-14">+{entry.tSeconds.toFixed(2)}s</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <DirectionBadge direction={entry.direction} />
            {entry.positive === false && <Badge variant="destructive" className="text-[10px]">NEGATIVE</Badge>}
            {entry.bytesHex && <span className="font-mono text-xs break-all">{entry.bytesHex}</span>}
          </div>
          <p className="text-sm mt-1 leading-snug">{entry.summary}</p>
        </div>
        {entry.breakdown && (expanded ? <ChevronUp className="h-4 w-4 shrink-0 mt-1 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 mt-1 text-muted-foreground" />)}
      </button>
      {expanded && entry.breakdown && (
        <div className="px-3 pb-3 text-xs" data-testid="uds-byte-breakdown">
          <div className="rounded-md border border-structure">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/60 hover:bg-muted/60">
                  <TableHead className="font-mono">Byte(s)</TableHead>
                  <TableHead className="font-mono">Hex</TableHead>
                  <TableHead className="font-mono">Field</TableHead>
                  <TableHead>Meaning</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entry.breakdown.map((b, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono whitespace-nowrap">{b.range}</TableCell>
                    <TableCell className="font-mono whitespace-nowrap">{b.hex}</TableCell>
                    <TableCell className="font-medium whitespace-nowrap">{b.label}</TableCell>
                    <TableCell className="text-muted-foreground">{b.meaning}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

const SessionLogPanel = ({ log, onClear }) => {
  const [expandedId, setExpandedId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'nearest' });
  }, [log.length]);

  return (
    <Card className="technical-card h-full flex flex-col" data-testid="uds-session-log">
      <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Session Log</CardTitle>
          <CardDescription>Newest entries at the bottom — click an entry to see its byte breakdown.</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={onClear} className="shrink-0" data-testid="uds-clear-log-btn">
          <Trash2 className="h-3.5 w-3.5" /> Clear
        </Button>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto space-y-2 max-h-[32rem]">
        {log.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-12 gap-2">
            <Info className="h-6 w-6" />
            <p className="text-sm">No requests sent yet. Build a request on the left and click Send.</p>
          </div>
        ) : (
          <>
            {log.map((entry) => (
              <LogEntry key={entry.id} entry={entry} expanded={expandedId === entry.id} onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)} />
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionLogPanel;
